import { Address, Dds, Email, Identification, PhoneNumber, TaxResidence } from 'types/PrivateIndividual';
import PartnerNameType from 'types/PartnerNameType';

export interface Partner {
  globalPartnerId: string;
  globalPartner: boolean;
  createdBy: string;
  status: string | PartnerStatus;
  lifecycleStage: string;
  agreementLifecycleState: string;
  priority: number;
  relationshipManager: string;
  correspondenceLanguage: string;
  partnerType: PartnerType;
  techId: string;
  revokeReason: string;
  identifications: Identification[];
  finder: boolean | null;
  isIntermediaryEfa: boolean | null;
  isIntermediaryEam: boolean | null;
  efaEpaCode: string;
  eamEpaCode: string;
  partnerNameDtos: PartnerName[];

  addresses: Address[];

  licenses: string[];

  contactDetails: ContactDetails;

  hitDetails: string;

  taxResidences: TaxResidence[];
  remarks: any;
  imported: boolean;
  politicalExposed: boolean | null;
  politicalExposedComment: string;
  brStatus: BrStatus;
  partnerStructureSubType?: PartnerStructureSubType;
}

export enum PartnerType {
  LEGAL_PARTNER = 'LEGAL_PARTNER',
  INDIVIDUAL_PARTNER = 'INDIVIDUAL_PARTNER',
  INSURANCE = 'INSURANCE',
  INTERMEDIARY = 'INTERMEDIARY',
  TRUST = 'TRUST',
  FOUNDATION = 'FOUNDATION',
}

export type PartnerTypeKeys = keyof typeof PartnerType;

export const PartnerStructureSubTypes = {
  DOMICILIARY_COMPANY: 'DOMICILIARY_COMPANY',
  FOUNDATION: 'FOUNDATION',
  FOUNDATION_DOMICILIARY: 'FOUNDATION_DOMICILIARY',
  FOUNDATION_OPERATING: 'FOUNDATION_OPERATING',
  INDIVIDUAL: 'INDIVIDUAL',
  INSTITUTIONAL_CLIENT: 'INSTITUTIONAL_CLIENT',
  OPERATING_COMPANY: 'OPERATING_COMPANY',
  STANDARD_LEGAL_ENTITY: 'STANDARD_LEGAL_ENTITY',
  TRUST: 'TRUST',
} as const;

export type PartnerStructureSubType = keyof typeof PartnerStructureSubTypes;

export interface PartnerName {
  name: string;
  nameType: PartnerNameType;
  alphabet: string;
}

export const getPartnerName = (partner: Partner, nameType: PartnerNameType) => {
  return partner.partnerNameDtos.find((nameDto: any) => nameDto.nameType === nameType)?.name || '';
};

export interface ContactDetails {
  phoneNumbers: PhoneNumber[];
  emails: Email[];
  dds: Dds[];
}

export enum BrStatus {
  DRAFT = 'DRAFT',
  IN_APPROVAL = 'IN_APPROVAL',
  READY_FOR_LINKING = 'READY_FOR_LINKING',
  ONBOARDING = 'ONBOARDING',
  OPERATING = 'OPERATING',
  IMPORTED = 'IMPORTED',
  CONVERTED = 'CONVERTED',
  REJECTED = 'REJECTED',
  WITHDRAWN = 'WITHDRAWN',
  DUPLICATE = 'DUPLICATE',
  DUPLICATE_CHECK_PENDING = 'DUPLICATE_CHECK_PENDING',
  CLOSED = 'CLOSED',
}

export enum PartnerStatus {
  DATA_CAPTURING = 'DATA_CAPTURING',
  READY_FOR_ONBOARDING = 'READY_FOR_ONBOARDING',
  IN_ONBOARDING = 'IN_ONBOARDING',
  WITHDRAWN = 'WITHDRAWN',
  REJECTED = 'REJECTED',
  ONBOARDED = 'ONBOARDED',
  DUPLICATE = 'DUPLICATE',
  IMPORTED = 'IMPORTED',
}

export const PARTNER_STATUS_DISPLAY_TEXT = {
  [PartnerStatus.REJECTED]: 'rejection',
  [PartnerStatus.WITHDRAWN]: 'withdrawal',
} as const;

export interface Employee {
  userId: string;
  employmentId: string;
  fullName: string;
  dataProtectionRegulationLevel: number;
  roles: EmployeeRole[];
  emailAddress: string;
  legalEntity: string;
  branchCode: string;
  assignedBusinessEntityCountryCode: string;
}

export enum EmployeeRole {
  RELATIONSHIP_MANAGER = 'RELATIONSHIP_MANAGER',
  TEAM_HEAD = 'FRONT_TEAM_HEAD',
  RM_ASSISTANT = 'RM_ASSISTANT',
  ITSO = 'ITSO',
}

export const LegalPartnerSubType = {
  STANDARD_LEGAL_ENTITY: 'STANDARD_LEGAL_ENTITY',
  INTERMEDIARY: 'INTERMEDIARY',
  INSURANCE: 'INSURANCE',
  TRUST: 'TRUST',
  FOUNDATION: 'FOUNDATION',
} as const;

export type LegalPartnerSubType = keyof typeof LegalPartnerSubType;
